import { Injectable } from '@angular/core';
import CryptoJS from 'crypto-js';
import { CompactEncrypt, compactDecrypt, importPKCS8, importSPKI } from 'jose';

export interface RsaOaepKeyPair {
  publicKey: string;
  privateKey: string;
}

/** Supported JWE algorithms for App Authenticator compact serialization. */
const JWE_ALG = 'RSA-OAEP-256';
const JWE_ENC = 'A256GCM';

@Injectable({
  providedIn: 'root'
})
export class CryptoService {
  // From https://github.com/brix/crypto-js/issues/468

  constructor() { }

  public static sha256(text: string): string {
    return CryptoJS.SHA256(text).toString(CryptoJS.enc.Hex);
  }

  /**
   * Generates an RSA-OAEP key pair for encrypt/decrypt.
   * Public and private keys are exported as base64 SPKI / PKCS8.
   */
  public static async generateRsaOaepKeyPair(): Promise<RsaOaepKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt']
    );

    const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const privateKeyBuffer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

    return {
      publicKey: CryptoService.arrayBufferToBase64(publicKeyBuffer),
      privateKey: CryptoService.arrayBufferToBase64(privateKeyBuffer),
    };
  }

  private static arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferView): string {
    const bytes = ArrayBuffer.isView(buffer)
      ? new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
      : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /** Wrap raw Base64 PKCS#8 for jose importPKCS8. */
  private static pkcs8Base64ToPem(base64: string): string {
    const body = (base64.match(/.{1,64}/g) ?? [base64]).join('\n');
    return `-----BEGIN PRIVATE KEY-----\n${body}\n-----END PRIVATE KEY-----`;
  }

  /** Wrap raw Base64 SPKI for jose importSPKI. */
  private static spkiBase64ToPem(base64: string): string {
    const body = (base64.match(/.{1,64}/g) ?? [base64]).join('\n');
    return `-----BEGIN PUBLIC KEY-----\n${body}\n-----END PUBLIC KEY-----`;
  }

  /**
   * True when value looks like JWE compact serialization (five Base64url segments).
   */
  public static isCompactJwe(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }
    const parts = value.trim().split('.');
    return parts.length === 5 && parts.every((p) => p.length > 0);
  }

  /**
   * Decrypts a compact JWE (RFC 7516) via jose.
   * Expected header: { "alg": "RSA-OAEP-256", "enc": "A256GCM" }
   * Private key is Base64 PKCS#8 (same as generateRsaOaepKeyPair).
   */
  public static async decryptCompactJwe(
    compactJwe: string,
    privateKeyBase64: string
  ): Promise<string> {
    const key = await importPKCS8(
      CryptoService.pkcs8Base64ToPem(privateKeyBase64),
      JWE_ALG
    );
    const { plaintext } = await compactDecrypt(compactJwe.trim(), key, {
      keyManagementAlgorithms: [JWE_ALG],
      contentEncryptionAlgorithms: [JWE_ENC],
    });
    return new TextDecoder().decode(plaintext);
  }

  /**
   * Encrypts plaintext as a compact JWE (RSA-OAEP-256 + A256GCM) via jose.
   * Public key is Base64 SPKI from generateRsaOaepKeyPair.
   */
  public static async encryptCompactJwe(
    plainText: string,
    publicKeyBase64: string
  ): Promise<string> {
    const key = await importSPKI(
      CryptoService.spkiBase64ToPem(publicKeyBase64),
      JWE_ALG
    );
    return new CompactEncrypt(new TextEncoder().encode(plainText))
      .setProtectedHeader({ alg: JWE_ALG, enc: JWE_ENC })
      .encrypt(key);
  }

  public static decryptAES(encryptedText: string, secret: string) {

    return CryptoJS.AES.decrypt(encryptedText, secret).toString(CryptoJS.enc.Utf8);

    // // From https://gist.github.com/schakko/2628689?permalink_comment_id=3321113#gistcomment-3321113
    // // From https://gist.github.com/chengen/450129cb95c7159cb05001cc6bdbf6a1
    // const cypher = Buffer.from(encryptedText, 'base64');
    // const salt = cypher.slice(8, 16);
    // const password = Buffer.concat([Buffer.from(secret, 'binary'), salt]);
    // const md5Hashes = [];
    // let digest = password;
    // for (let i = 0; i < 3; i++) {
    //   md5Hashes[i] = crypto.createHash('md5').update(digest).digest();
    //   digest = Buffer.concat([md5Hashes[i], password]);
    // }
    // const key = Buffer.concat([md5Hashes[0], md5Hashes[1]]);
    // const iv = md5Hashes[2];
    // const contents = cypher.slice(16);
    // const decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);

    // return decipher.update(contents) + decipher.final();
  }


  public static encryptAES(plainText: string, secret: string) {

    return CryptoJS.AES.encrypt(plainText, secret).toString();

    // const salt = crypto.randomBytes(8);
    // const password = Buffer.concat([Buffer.from(secret, 'binary'), salt]);
    // const hash = [];
    // let digest = password;
    // for (let i = 0; i < 3; i++) {
    //   hash[i] = crypto.createHash('md5').update(digest).digest();
    //   digest = Buffer.concat([hash[i], password]);
    // }
    // const keyDerivation = Buffer.concat(hash);
    // const key = keyDerivation.subarray(0, 32);
    // const iv = keyDerivation.subarray(32);
    // const cipher = crypto.createCipheriv('aes-256-cbc', key, iv);
    // return Buffer.concat([
    //   Buffer.from('Salted__', 'utf8'),
    //   salt,
    //   cipher.update(plainText),
    //   cipher.final()
    // ]).toString('base64');
  }

}
